import React, { useEffect, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import {
  selectCurrentOrganizationId,
  selectSubOrganizationsData,
} from '../../store/organization/organization-slice-selectors';
import { useAppDispatch, useAppSelector } from '../../store';
import OrganizationRole from '../../models/enums/organization-roles';
import { useNavigate } from 'react-router-dom';
import CustomTable from '../misc/CustomTable';
import { navigateSphereWithFilters } from '@common/enums/query-params/filters';
import { selectIsUserAdminOrHigher } from '../../store/general/general-slice-selectors';
import DeleteDialog from './DeleteDialog';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { DeleteIcon, OpenInNewTabIcon } from '@common/utils/icons/icons';
import Stack from '@mui/material/Stack';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import { resetOrganizationsRestState } from '../../store/organization/organization-slice';

const OrganizationsTable: React.FC<{ role: OrganizationRole }> = ({ role }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const organizations = useAppSelector((state) => selectSubOrganizationsData(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));

  const [deletedOrganizationId, setDeletedOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    if (organizations.rest.deleteStatus === HttpFetchStatus.Success) {
      setDeletedOrganizationId(null);
    }
  }, [organizations.rest.deleteStatus]);

  const rowClickHandler = (id: string) => {
    navigate(navigateSphereWithFilters(id, true));
  };

  const newTabClickHandler = (id: string) => {
    const url = navigateSphereWithFilters(id, true);
    window.open(url.toString(), '_blank');
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('table-header-name') as string, width: 250 },
    { field: 'robotCount', headerName: t('table-header-robot-count') as string, width: 100 },
    { field: 'userCount', headerName: t('table-header-user-count') as string, width: 100 },
  ];
  if (role !== OrganizationRole.LOCATION) {
    columns.push({
      field: 'subOrganizationCount',
      headerName: t('table-header-sub-organization-count') as string,
    });
  }

  const openDeleteDialogClickHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    organizationId: string,
  ) => {
    event.stopPropagation();

    setDeletedOrganizationId(organizationId);
  };

  const closeDeleteDialogHandler = () => {
    setDeletedOrganizationId(null);
  };

  const closeRestHandler = () => {
    dispatch(
      resetOrganizationsRestState({
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
  };

  if (isAdmin) {
    columns.push({
      field: 'actions',
      headerName: '',
      renderCell: (params) => {
        return (
          <Stack direction="row" width="100%" justifyContent="end">
            <TooltipIconButton
              tooltip={t('button-delete')}
              data-testid="delete-organization-button"
              icon={<DeleteIcon />}
              tooltipProps={{ placement: 'left' }}
              color="error"
              onClick={(event) => openDeleteDialogClickHandler(event, params.id as string)}
            />
          </Stack>
        );
      },
    });
  }

  const shownOrganizations = (organizations.data ?? []).filter(
    (org) => org.role === role && org.parentId === currentOrganizationId,
  );

  return (
    <React.Fragment>
      <CustomTable
        getStatus={organizations.rest.getAllOrganizationsStatus}
        loadingMessage={t('loading-organizations')}
        dataLoaded={organizations.data != null}
        failedToLoadMessage={
          organizations.rest.getAllOrganizationsError?.message ?? t('error-loading-organizations')
        }
        data={shownOrganizations}
        emptyDataMessage={t('info-no-' + role)}
        columns={columns}
        rowClickHandler={rowClickHandler}
        hideFooter
        contextMenuOptions={[
          {
            title: t('button-open-in-new-tab'),
            icon: <OpenInNewTabIcon color="action" />,
            action: newTabClickHandler,
          },
        ]}
      />
      {deletedOrganizationId != null && (
        <DeleteDialog
          organization={organizations.data?.find((org) => org.id === deletedOrganizationId)}
          open={deletedOrganizationId != null}
          onClose={closeDeleteDialogHandler}
        />
      )}
      <CustomSnackbar
        open={organizations.rest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting')}
        onClose={closeRestHandler}
      />
    </React.Fragment>
  );
};

export default OrganizationsTable;
